/**
 * <breadcrumb-trail> — loaded by snippets/breadcrumbs.liquid wherever a trail
 * renders, rather than from main.js, which every page loads (ADR-0023).
 */
if (!customElements.get('breadcrumb-trail')) {
  /**
   * Keeps a breadcrumb trail on one line and fills it (ADR-0023). The current
   * page takes whatever room the levels before it leave, its name cut with an
   * ellipsis only at the row's end. A level collapses only when the trail would
   * leave the current page less than its promised room
   * (`--breadcrumb-current-min`, or its whole name when shorter): the levels
   * furthest from it hide first, one at a time from the one after Home, behind
   * a "…" button. Home and the parent always show; if even they leave too
   * little room, the current page gives way down to 6em before the parent is cut
   * (`is-tight`).
   * The button expands the full trail over as many lines as it needs.
   *
   * Without this element the list wraps and shows every level; the JSON-LD
   * BreadcrumbList is the full trail either way.
   */
  class BreadcrumbTrail extends theme.BaseElement {
    connectedCallback() {
      this.list = this.querySelector('.breadcrumbs-list');
      this.more = this.querySelector('.breadcrumbs-list__more-item');
      if (!this.list || !this.more) return;

      this.on(this.more.querySelector('button'), 'click', () => this.expand());
      // Re-fit when the row changes width, and when the JSON category source
      // inserts its ancestor crumbs after load (category-breadcrumbs.js).
      this.observe(new ResizeObserver(() => this.fit()), this.list);
      this.observe(new MutationObserver(() => this.fit()), this.list, { childList: true });
      this.fit();
    }

    /** The levels that may collapse: between Home and the parent of the current page. */
    collapsible() {
      const items = [...this.list.children].filter((li) => li !== this.more);
      return items.slice(1, -2);
    }

    overflows() {
      return this.list.scrollWidth > this.list.clientWidth + 1;
    }

    fit() {
      if (this.expanded) {
        this.updateExtraHeight();
        return;
      }
      // Measured with the current page held at its promised room (main.css,
      // `is-measuring`): the question is whether the levels leave it that much,
      // not whether its whole name fits.
      const collapsible = this.collapsible();
      this.classList.remove('is-tight');
      this.classList.add('is-measuring');
      collapsible.forEach((li) => {
        li.hidden = false;
      });
      this.more.hidden = true;

      let fits = !this.overflows();
      if (!fits && collapsible.length > 0) {
        this.more.hidden = false;
        for (const li of collapsible) {
          li.hidden = true;
          if (!this.overflows()) {
            fits = true;
            break;
          }
        }
      }
      this.classList.remove('is-measuring');
      this.classList.toggle('is-tight', !fits);
    }

    expand() {
      const collapsedHeight = this.list.offsetHeight;
      this.expanded = true;
      this.classList.remove('is-tight');
      this.classList.add('is-expanded');
      const revealed = this.collapsible().filter((li) => li.hidden);
      revealed.forEach((li) => {
        li.hidden = false;
      });
      this.more.hidden = true;
      this.collapsedHeight = collapsedHeight;
      this.updateExtraHeight();
      // The button that had focus is gone: hand it to the first level it revealed.
      revealed[0]?.querySelector('a')?.focus();
    }

    /**
     * The product page's column divider is drawn up past a one-line breadcrumb
     * row (product-page.css); an expanded trail is taller, by this much.
     */
    updateExtraHeight() {
      const section = this.closest('.product-main');
      if (!section || this.collapsedHeight === undefined) return;
      const extra = Math.max(0, this.list.offsetHeight - this.collapsedHeight);
      section.style.setProperty('--breadcrumbs-extra-height', `${extra}px`);
    }
  }

  customElements.define('breadcrumb-trail', BreadcrumbTrail);
}
